import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import AppHeader from "../components/AppHeader";
import DisclaimerBanner from "../components/DisclaimerBanner";
import SiteFooter from "../components/SiteFooter";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  ArrowUpRight, ChevronLeft, ExternalLink, Info, Search, Library, ScrollText,
} from "lucide-react";
import RelatedCaseSearch from "../components/RelatedCaseSearch";

const inputCls =
  "rounded-none border-slate-300 focus:border-slate-900 focus:ring-1 focus:ring-slate-900";

export default function LegalLibrary() {
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ resource_types: {}, categories: {}, notice: "" });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ jurisdiction: "", scope: "", resource_type: "", category: "", q: "" });

  const load = async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
      const [r, m] = await Promise.all([
        api.get("/legal-library", { params }),
        api.get("/legal-library-meta"),
      ]);
      setItems(r.data);
      setMeta(m.data);
    } catch (e) {
      toast.error("Could not load legal library");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const jurisdictionOptions = useMemo(() => {
    const s = new Set(items.map((x) => x.jurisdiction_code));
    return Array.from(s).sort();
  }, [items]);

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <DisclaimerBanner />
      <AppHeader />
      <main className="max-w-[1300px] mx-auto px-6 py-10 md:py-14 flex-1 w-full" data-testid="legal-library-page">
        <div className="flex items-center gap-4 mb-6">
          <Link to="/resources" className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900 underline underline-offset-4">
            <ChevronLeft className="w-4 h-4" /> Back to Resources
          </Link>
        </div>
        <header className="mb-8 max-w-3xl">
          <div className="label-tiny mb-3">Library</div>
          <h1 className="font-serif-display text-4xl md:text-5xl text-slate-900 leading-tight">
            <Library className="inline-block w-9 h-9 mr-2 -mt-2 text-slate-700" strokeWidth={1.75} />
            Legal Research Library
          </h1>
          <p className="mt-4 text-slate-600">
            Curated links to constitutions, statutes, rules of civil procedure, court forms, and other
            official government resources for federal courts and every U.S. state and D.C.
          </p>
        </header>

        <div className="border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900 flex gap-3 mb-8" data-testid="legal-library-notice">
          <Info className="w-5 h-5 mt-0.5 shrink-0 text-amber-700" />
          <div>{meta.notice}</div>
        </div>

        <div className="border border-slate-200 p-4 md:p-5 mb-6 grid grid-cols-1 md:grid-cols-6 gap-3" data-testid="legal-library-filters">
          <div className="md:col-span-2">
            <Label className="label-tiny mb-1.5 block">Keyword</Label>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                value={filters.q}
                onChange={(e) => setFilters({ ...filters, q: e.target.value })}
                onKeyDown={(e) => { if (e.key === "Enter") load(); }}
                placeholder="e.g. civil procedure, statute of limitations"
                className={`${inputCls} pl-9`}
                data-testid="ll-q"
              />
            </div>
          </div>
          <FilterSelect label="Scope" value={filters.scope} onChange={(v) => setFilters({ ...filters, scope: v })} options={{ federal: "Federal", state: "State" }} testid="ll-scope" />
          <FilterSelect label="Jurisdiction" value={filters.jurisdiction} onChange={(v) => setFilters({ ...filters, jurisdiction: v })} options={Object.fromEntries(jurisdictionOptions.map((j) => [j, j]))} testid="ll-jurisdiction" />
          <FilterSelect label="Resource type" value={filters.resource_type} onChange={(v) => setFilters({ ...filters, resource_type: v })} options={meta.resource_types} testid="ll-type" />
          <FilterSelect label="Category" value={filters.category} onChange={(v) => setFilters({ ...filters, category: v })} options={meta.categories} testid="ll-category" />
          <div className="md:col-span-6 flex gap-2 pt-1">
            <Button onClick={load} className="rounded-none bg-slate-900 hover:bg-slate-800 text-white" data-testid="ll-apply">Apply filters</Button>
            <Button
              variant="outline"
              onClick={() => { setFilters({ jurisdiction: "", scope: "", resource_type: "", category: "", q: "" }); setTimeout(load, 50); }}
              className="rounded-none border-slate-300"
              data-testid="ll-clear"
            >
              Clear
            </Button>
            <span className="ml-auto self-center text-xs text-slate-500">
              {loading ? "Loading…" : `${items.length} resource${items.length === 1 ? "" : "s"}`}
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3" data-testid="legal-library-grid">
          {items.map((r) => (
            <div
              key={r.id}
              className="border border-slate-200 -mt-px -ml-px bg-white p-5 md:p-6 transition-shadow duration-200 hover:z-10 hover:shadow-[0_6px_18px_-12px_rgba(15,23,42,0.15)]"
              data-testid={`legal-resource-${r.id}`}
            >
              <div className="flex items-start gap-2 mb-2 flex-wrap">
                <Badge variant="outline" className="rounded-none border-slate-300 font-mono-tech text-[10px]">{r.jurisdiction_code}</Badge>
                <Badge variant="outline" className="rounded-none border-slate-300 font-mono-tech text-[10px]">
                  {meta.resource_types?.[r.resource_type] || r.resource_type}
                </Badge>
                {r.scope === "federal" && (
                  <Badge variant="outline" className="rounded-none border-blue-300 bg-blue-50 text-blue-800 font-mono-tech text-[10px]">Federal</Badge>
                )}
              </div>
              <div className="flex items-start gap-2 mb-1">
                <ScrollText className="w-4 h-4 mt-1 text-slate-600 shrink-0" strokeWidth={1.75} />
                <div className="font-serif-display text-lg text-slate-900 leading-snug">{r.title}</div>
              </div>
              <div className="text-xs text-slate-600 mb-2">{r.issuing_authority}</div>
              <p className="text-sm text-slate-700 leading-relaxed">{r.summary}</p>
              <div className="mt-3 flex flex-wrap gap-1">
                {(r.categories || []).slice(0, 6).map((c) => (
                  <Badge key={c} variant="outline" className="rounded-none border-slate-200 text-slate-600 text-[10px]">
                    {meta.categories?.[c] || c}
                  </Badge>
                ))}
              </div>
              <div className="mt-4 flex flex-wrap items-center gap-2">
                <a
                  href={r.official_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-sm font-medium text-white bg-slate-900 hover:bg-slate-800 px-3 py-1.5"
                  data-testid={`open-resource-${r.id}`}
                >
                  Open official source <ArrowUpRight className="w-3.5 h-3.5" />
                </a>
                <a
                  href={r.official_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-900 underline underline-offset-4 ml-auto"
                >
                  Source <ExternalLink className="w-3 h-3" />
                </a>
              </div>
              <div className="text-[11px] text-slate-500 mt-2 tabular-nums">
                Last verified: {r.last_verified_at || "—"}
              </div>
            </div>
          ))}
          {!loading && items.length === 0 && (
            <div className="col-span-full border border-slate-200 p-10 text-center text-slate-500" data-testid="legal-library-empty">
              No resources match your filters.
            </div>
          )}
        </div>

        <div className="mt-12">
          <RelatedCaseSearch />
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}

function FilterSelect({ label, value, onChange, options, testid }) {
  return (
    <div>
      <Label className="label-tiny mb-1.5 block">{label}</Label>
      <Select value={value || "__all__"} onValueChange={(v) => onChange(v === "__all__" ? "" : v)}>
        <SelectTrigger className={inputCls} data-testid={testid}>
          <SelectValue placeholder="All" />
        </SelectTrigger>
        <SelectContent className="max-h-[320px]">
          <SelectItem value="__all__">All</SelectItem>
          {Object.entries(options || {}).map(([k, v]) => (
            <SelectItem key={k} value={k}>{v}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
